/**
 * Parse an assistant message string into renderable segments. Mirrors the
 * iOS MessageSegmentParser logic:
 *
 *   - text  → plain markdown to display in a chat bubble
 *   - spot  → poker spot card (structured JSON the model emits)
 *   - choices → multiple-choice button row
 *   - hidden  → skill_signal blocks or malformed spots; claimed (so they
 *               don't leak into text) but never rendered
 *
 * Robust to whether the model wraps its JSON in ```spot fences or emits
 * the JSON bare. Suppresses unclosed fences mid-stream so partial JSON
 * never flashes before the SpotCard pops in.
 */

import { isCanonicalPosition } from "@/features/chat/lib/seatLayout";
import type {
  ChoicesPayload,
  ContentSegment,
  SpotPayload,
} from "@/shared/models";

/** Public entry — pass an assistant message body, get rendered segments. */
export function parseSegments(raw: string): ContentSegment[] {
  const blocks = findStructuredBlocks(raw);

  if (blocks.length === 0) {
    const cleaned = suppressUnclosedFences(raw);
    return cleaned.trim() ? [{ kind: "text", text: cleaned }] : [];
  }

  const segments: ContentSegment[] = [];
  let cursor = 0;

  for (const block of blocks) {
    if (cursor < block.start) {
      const pre = stripAdjacentFenceOpen(raw.slice(cursor, block.start));
      if (pre.trim()) segments.push({ kind: "text", text: pre });
    }
    if (block.segment.kind !== "hidden") {
      segments.push(block.segment);
    }
    cursor = block.end;
    cursor = skipAdjacentFenceClose(raw, cursor);
  }

  if (cursor < raw.length) {
    const trailing = suppressUnclosedFences(raw.slice(cursor));
    if (trailing.trim()) segments.push({ kind: "text", text: trailing });
  }

  return segments;
}

interface FoundBlock {
  start: number;
  end: number;
  segment: ContentSegment;
}

function findStructuredBlocks(text: string): FoundBlock[] {
  const results: FoundBlock[] = [];
  let i = 0;
  while (i < text.length) {
    if (text[i] === "{") {
      const close = findMatchingCloseBrace(text, i);
      if (close !== null) {
        const body = text.slice(i, close + 1);
        if (body.includes('"type"')) {
          const range = { start: i, end: close + 1 };
          if (body.includes('"spot"')) {
            const decoded = safeJsonParse(body);
            const spot = isValidSpot(decoded) ? (decoded as SpotPayload) : null;
            if (spot) {
              results.push({ ...range, segment: { kind: "spot", spot } });
            } else {
              // Block announced itself as a spot but failed schema check
              // — drop as hidden so the SpotCard never gets bad data.
              // Mirrors iOS's typed-decoder failure path.
              results.push({ ...range, segment: { kind: "hidden", payload: { raw: body } } });
            }
            i = close + 1;
            continue;
          }
          if (body.includes('"choices"')) {
            const decoded = safeJsonParse(body);
            const choices = isValidChoices(decoded) ? (decoded as ChoicesPayload) : null;
            if (choices) {
              results.push({ ...range, segment: { kind: "choices", choices } });
            } else {
              results.push({ ...range, segment: { kind: "hidden", payload: { raw: body } } });
            }
            i = close + 1;
            continue;
          }
          if (body.includes('"skill_signal"')) {
            results.push({ ...range, segment: { kind: "hidden", payload: { raw: body } } });
            i = close + 1;
            continue;
          }
        }
      }
    }
    i++;
  }
  return results;
}

function findMatchingCloseBrace(text: string, openIndex: number): number | null {
  let depth = 0;
  let inString = false;
  let escapeNext = false;
  for (let i = openIndex; i < text.length; i++) {
    const c = text[i];
    if (escapeNext) {
      escapeNext = false;
    } else if (inString && c === "\\") {
      escapeNext = true;
    } else if (c === '"') {
      inString = !inString;
    } else if (!inString) {
      if (c === "{") depth++;
      else if (c === "}") {
        depth--;
        if (depth === 0) return i;
      }
    }
  }
  return null;
}

function safeJsonParse(s: string): unknown {
  try {
    return JSON.parse(s);
  } catch {
    return null;
  }
}

/** Minimal Spot schema check — mirrors iOS's typed JSONDecoder failure
 *  semantics. Doesn't validate every field; just enough that the
 *  SpotCard can render without crashing.
 *
 *  Also enforces the canonical-position contract: spot.position and
 *  every non-`"Hero"` history actor must be one of the 9 canonical
 *  seat labels. Off-contract spots become `hidden` segments — same
 *  fail-closed path as malformed JSON. Mirrors the backend's
 *  `spot_parser._parse_one` gate (drill_meta.is_canonical_position). */
function isValidSpot(value: unknown): boolean {
  if (!value || typeof value !== "object") return false;
  const v = value as Record<string, unknown>;
  if (v.type !== "spot") return false;
  if (typeof v.position !== "string") return false;
  if (!isCanonicalPosition(v.position)) return false;
  if (!Array.isArray(v.hand)) return false;
  if (!Array.isArray(v.board)) return false;
  if (!Array.isArray(v.history)) return false;
  if (typeof v.street !== "string") return false;
  if (typeof v.pot !== "number") return false;
  if (typeof v.stacks !== "object" || v.stacks === null) return false;
  for (const entry of v.history) {
    if (!entry || typeof entry !== "object") continue;
    const actor = (entry as Record<string, unknown>).actor;
    if (actor === "Hero") continue;
    if (typeof actor !== "string" || !isCanonicalPosition(actor)) return false;
  }
  return true;
}

const _DIFFICULTIES = new Set(["foundational", "default", "edge_case"]);

/** A choices option that launches a drill: `{label, drill_target}` with
 *  non-empty string fields (+ optional valid `drill_difficulty`). Structural
 *  only — the canonical-skill-id check happens at render time so an unknown
 *  target degrades one option to a re-send rather than failing the whole block. */
function isDrillChoiceOption(o: unknown): boolean {
  if (!o || typeof o !== "object") return false;
  const r = o as Record<string, unknown>;
  if (typeof r.label !== "string" || r.label.length === 0) return false;
  if (typeof r.drill_target !== "string" || r.drill_target.length === 0) return false;
  if (r.drill_difficulty !== undefined && !_DIFFICULTIES.has(r.drill_difficulty as string))
    return false;
  return true;
}

function isValidChoices(value: unknown): boolean {
  if (!value || typeof value !== "object") return false;
  const v = value as Record<string, unknown>;
  if (v.type !== "choices") return false;
  if (!Array.isArray(v.options)) return false;
  // Each option is a non-empty string OR a structurally-valid drill option.
  return v.options.every(
    (o) => (typeof o === "string" && o.length > 0) || isDrillChoiceOption(o),
  );
}

function stripAdjacentFenceOpen(text: string): string {
  return text.replace(/```[a-zA-Z0-9_-]*\s*$/, "");
}

function skipAdjacentFenceClose(text: string, from: number): number {
  const remaining = text.slice(from);
  const match = remaining.match(/^\s*```\s*/);
  if (match) return from + match[0].length;
  return from;
}

function suppressUnclosedFences(text: string): string {
  const positions: number[] = [];
  let i = 0;
  while ((i = text.indexOf("```", i)) !== -1) {
    positions.push(i);
    i += 3;
  }
  if (positions.length % 2 === 0) return text;
  return text.slice(0, positions[positions.length - 1]!);
}

